import type { Id } from "@repo/convex/dataModel";
import {
  buildProjectNavigationTree,
  type FeatureFlagMap,
  type ProjectNavigationTreeNode,
} from "@repo/shared";
import { Folder, FolderTree } from "lucide-react";
import { type ElementType, useMemo } from "react";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { resolveEntityDisplayIcon } from "../lib/entity-icon-utils";
import { buildFrameworkEntityAliases, getTreeItemType } from "../lib/framework-registry";
import { resolveFrameworkIcon } from "../lib/icon-registry";
import { getVisibleProjectLevelNavItems } from "../lib/project-level-nav-items";
import type { TreeItem } from "../types";
import {
  useProjectEntities,
  useProjectFrameworkStructure,
  useProjectRelations,
  useProjectTreeFolders,
} from "./useProjectData";

/**
 * The pinned project-level block of the sidebar tree, in `PROJECT_LEVEL_NAV_ITEMS` order and
 * carrying only the items `flags` allows. Pure: the caller supplies the map.
 */
export function buildProjectLevelNavigationTree(flags: FeatureFlagMap): TreeItem[] {
  return getVisibleProjectLevelNavItems(flags).map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    type: "project-link",
    path: item.path,
  }));
}

function resolveTreeNodeIcon(node: ProjectNavigationTreeNode): ElementType {
  if (node.type === "root-folder") {
    return resolveFrameworkIcon(node.iconKey, FolderTree);
  }

  if (node.type === "project-tree-folder") {
    return Folder;
  }

  if (node.type === "entity-collection") {
    return resolveEntityDisplayIcon({
      entityTypeKey: node.entityTypeKey ?? "",
      iconKey: node.iconKey,
    });
  }

  return resolveEntityDisplayIcon({
    entityTypeKey: node.entityTypeKey ?? "",
    iconKey: node.iconKey,
  });
}

function mapNavigationNodeToTreeItem(
  node: ProjectNavigationTreeNode,
  catalog: Parameters<typeof getTreeItemType>[0]
): TreeItem {
  return {
    id: node.id,
    label: node.label,
    icon: resolveTreeNodeIcon(node),
    path: node.path,
    type: node.type === "entity" ? getTreeItemType(catalog, node.entityTypeKey ?? "") : node.type,
    metadata: {
      frameworkNodeKey: node.frameworkNodeKey,
      frameworkNodeKind: node.frameworkNodeKind,
      rootFolderKey: node.rootFolderKey,
      rootFolderSlug: node.rootFolderSlug,
      entityTypeKey: node.entityTypeKey,
      entityAlias: node.entityAlias,
      entityId: node.entityId,
      entitySlug: node.entitySlug,
      collectionSlug: node.collectionSlug,
      collectionNodeKey: node.collectionNodeKey,
      projectTreeFolderId: node.projectTreeFolderId,
      projectTreeFolderKind: node.projectTreeFolderKind,
      allowProjectTreeFolderCreate: node.allowProjectTreeFolderCreate,
      parentFrameworkNodeKey: node.parentFrameworkNodeKey,
      parentEntities: node.parentEntities,
    },
    children: node.children?.map((child) => mapNavigationNodeToTreeItem(child, catalog)),
  };
}

/**
 * Builds a dynamic project tree driven by the project's assigned framework structure.
 *
 * The framework compiled structure defines root-folder aliases/icons, entity aliases/icons,
 * and topology rules. Shared framework navigation builds the entity/folder tree and the web app
 * maps the serializable nodes to TreeItem presentation concerns only.
 */
export function useProjectTree(projectId: Id<"projects"> | null) {
  const { flags: featureFlags } = useFeatureFlags();
  const entities = useProjectEntities(projectId);
  const frameworkStructure = useProjectFrameworkStructure(projectId);
  const relations = useProjectRelations(projectId);
  const projectTreeFolders = useProjectTreeFolders(projectId);
  const aliases = buildFrameworkEntityAliases(frameworkStructure ?? null);

  const isLoading =
    entities === undefined ||
    frameworkStructure === undefined ||
    relations === undefined ||
    projectTreeFolders === undefined;

  const { tree, configError } = useMemo(() => {
    if (isLoading || !entities || !projectTreeFolders) {
      return { tree: null, configError: null };
    }

    if (!frameworkStructure) {
      return {
        tree: null,
        configError: "Framework configuration missing: no framework structure found for project.",
      };
    }

    if ((frameworkStructure.rootFolders ?? []).length === 0) {
      return {
        tree: null,
        configError: "Framework configuration invalid: no root folders are configured.",
      };
    }

    if (!aliases) {
      return {
        tree: null,
        configError: "Framework configuration invalid: entity aliases could not be resolved.",
      };
    }

    const navigationTree = buildProjectNavigationTree({
      structure: frameworkStructure,
      aliases,
      entities,
      projectTreeFolders,
      relations: relations ?? [],
    });

    return {
      tree: {
        rootFolders: [
          ...buildProjectLevelNavigationTree(featureFlags),
          ...navigationTree.rootFolders.map((node) =>
            mapNavigationNodeToTreeItem(node, frameworkStructure.compiledCatalog)
          ),
        ],
      },
      configError: null,
    };
  }, [
    aliases,
    entities,
    featureFlags,
    frameworkStructure,
    isLoading,
    projectTreeFolders,
    relations,
  ]);

  return { tree, isLoading, configError };
}
